import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  InlineStack,
  BlockStack,
  Text,
  Badge,
  Button,
  Divider,
  TextField,
  Select,
  EmptyState,
  Spinner,
  Box,
  DataTable
} from '@shopify/polaris';
import {
  ArrowLeftMinor
} from '@shopify/polaris-icons';

// Import services and constants
import { ticketsService } from '../services/ticketsService';
import AssignTicketModal from '../components/modals/AssignTicketModal';
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_TYPE_LABELS,
  TICKET_CATEGORY_LABELS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  TICKET_STATUSES,
  TICKET_SOURCE_LABELS
} from '../constants/ticketConstants';

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  // State management
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [addingComment, setAddingComment] = useState(false);

  // Status update state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Load ticket details
  const loadTicket = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ticketsService.getTicket(ticketId);
      if (response.success) {
        setTicket(response.data);
        setNewStatus(response.data.status);

        // Update localStorage for App.js to use
        localStorage.setItem('ticketDetailData', JSON.stringify({
          ticketId: response.data.ticket_id,
          status: TICKET_STATUS_LABELS[response.data.status],
          createdAt: formatDate(response.data.created_at)
        }));
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
      alert('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Initial load
  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  // Setup event listeners for App.js actions
  useEffect(() => {
    const handleRefreshEvent = () => {
      loadTicket();
    };

    const handleBackEvent = () => {
      navigate('/tickets');
    };

    window.addEventListener('ticketDetailRefresh', handleRefreshEvent);
    window.addEventListener('ticketDetailBack', handleBackEvent);

    return () => {
      window.removeEventListener('ticketDetailRefresh', handleRefreshEvent);
      window.removeEventListener('ticketDetailBack', handleBackEvent);
    };
  }, [loadTicket, navigate]);

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === ticket.status) {
      alert('Please select a different status');
      return;
    }

    try {
      setUpdating(true);
      await ticketsService.updateTicketStatus(ticketId, newStatus, statusReason);
      await loadTicket();
      setStatusModalOpen(false);
      setStatusReason('');
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.message || 'Failed to update ticket status');
    } finally {
      setUpdating(false);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      setAddingComment(true);
      await ticketsService.addComment(ticketId, newComment, commentInternal);
      setNewComment('');
      setCommentInternal(false);
      await loadTicket();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert(error.message || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Handle file selection (multiple files)
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];

    for (const file of files) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file and will be skipped`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is larger than 10MB and will be skipped`);
        continue;
      }

      validFiles.push(file);
    }

    setSelectedFiles(validFiles);
  };

  // Handle file upload (multiple files)
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file');
      return;
    }

    try {
      setUploadingFile(true);

      // Upload files sequentially
      for (const file of selectedFiles) {
        await ticketsService.uploadAttachment(ticketId, file);
      }

      setSelectedFiles([]);
      // Reset file input
      document.getElementById('file-upload-input').value = '';
      await loadTicket();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(error.message || 'Failed to upload files');
    } finally {
      setUploadingFile(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  // Loading state
  if (loading) {
    return (
      <Card>
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Card>
    );
  }

  // Error state
  if (!ticket) {
    return (
      <Card>
        <EmptyState
          heading="Ticket not found"
          action={{
            content: 'Back to Tickets',
            onAction: () => navigate('/tickets')
          }}
        >
          <p>The ticket you're looking for doesn't exist or has been deleted.</p>
        </EmptyState>
      </Card>
    );
  }

  // Status options for dropdown
  const statusOptions = Object.entries(TICKET_STATUSES).map(([key, value]) => ({
    label: TICKET_STATUS_LABELS[value],
    value: value
  }));

  return (
    <>
      {/* Header Card with Basic Info */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="200">
              <InlineStack gap="200" blockAlign="center">
                <Button
                  icon={ArrowLeftMinor}
                  onClick={() => navigate('/tickets')}
                  accessibilityLabel="Back to tickets"
                />
                <Text variant="headingLg" as="h2">{ticket.title}</Text>
              </InlineStack>
              <InlineStack gap="300">
                <Badge tone={TICKET_STATUS_COLORS[ticket.status]}>
                  {TICKET_STATUS_LABELS[ticket.status]}
                </Badge>
                <Badge tone={PRIORITY_COLORS[ticket.priority]}>
                  {PRIORITY_LABELS[ticket.priority]}
                </Badge>
                <Text variant="bodySm" tone="subdued">
                  {TICKET_TYPE_LABELS[ticket.type]}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {TICKET_CATEGORY_LABELS[ticket.category]}
                </Text>
              </InlineStack>
            </BlockStack>

            <Button
              onClick={() => setStatusModalOpen(!statusModalOpen)}
              loading={updating}
            >
              Update Status
            </Button>
          </InlineStack>

          {statusModalOpen && (
            <>
              <Divider />
              <BlockStack gap="300">
                <Select
                  label="New Status"
                  options={statusOptions}
                  value={newStatus}
                  onChange={(value) => setNewStatus(value)}
                />
                <TextField
                  label="Reason (optional)"
                  value={statusReason}
                  onChange={(value) => setStatusReason(value)}
                  placeholder="Why are you changing the status?"
                  autoComplete="off"
                />
                <InlineStack gap="200">
                  <Button primary onClick={handleStatusUpdate} loading={updating}>
                    Update
                  </Button>
                  <Button onClick={() => setStatusModalOpen(false)}>
                    Cancel
                  </Button>
                </InlineStack>
              </BlockStack>
            </>
          )}
        </BlockStack>
      </Card>

      {/* Customer Information */}
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">Customer Information</Text>
          <Divider />
          <InlineStack gap="600">
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">Name</Text>
              <Text variant="bodyMd">{ticket.customer?.name || 'N/A'}</Text>
            </BlockStack>
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">Email</Text>
              <Text variant="bodyMd">{ticket.customer?.email || 'N/A'}</Text>
            </BlockStack>
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">Phone</Text>
              <Text variant="bodyMd">{ticket.customer?.phone || 'N/A'}</Text>
            </BlockStack>
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Ticket Details */}
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">Ticket Details</Text>
          <Divider />
          <BlockStack gap="300">
            <Box>
              <Text variant="bodyMd" fontWeight="semibold">Description</Text>
              <Box paddingBlockStart="200">
                <Text variant="bodyMd">{ticket.description}</Text>
              </Box>
            </Box>

            {ticket.related_order_id && (
              <Box>
                <Text variant="bodyMd" fontWeight="semibold">Related Order</Text>
                <Box paddingBlockStart="200">
                  <Button
                    plain
                    onClick={() => navigate(`/orders/${ticket.related_order_id}`)}
                  >
                    {ticket.related_order_id}
                  </Button>
                </Box>
              </Box>
            )}

            {ticket.tags && ticket.tags.length > 0 && (
              <Box>
                <Text variant="bodyMd" fontWeight="semibold">Tags</Text>
                <Box paddingBlockStart="200">
                  <InlineStack gap="200">
                    {ticket.tags.map((tag, index) => (
                      <Badge key={index}>{tag}</Badge>
                    ))}
                  </InlineStack>
                </Box>
              </Box>
            )}

            {ticket.source && (
              <Box>
                <Text variant="bodyMd" fontWeight="semibold">Source</Text>
                <Box paddingBlockStart="200">
                  <Badge tone="info">
                    {TICKET_SOURCE_LABELS[ticket.source] || ticket.source}
                  </Badge>
                </Box>
              </Box>
            )}

            <InlineStack gap="600">
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="semibold">Created</Text>
                <Text variant="bodySm" tone="subdued">{formatDate(ticket.created_at)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="semibold">Updated</Text>
                <Text variant="bodySm" tone="subdued">{formatDate(ticket.updated_at)}</Text>
              </BlockStack>
              {ticket.resolved_at && (
                <BlockStack gap="100">
                  <Text variant="bodyMd" fontWeight="semibold">Resolved</Text>
                  <Text variant="bodySm" tone="subdued">{formatDate(ticket.resolved_at)}</Text>
                </BlockStack>
              )}
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Assignment Information */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h3">Assignment</Text>
            <Button onClick={() => setAssignModalOpen(true)}>
              {ticket.assigned_to ? 'Reassign Ticket' : 'Assign Ticket'}
            </Button>
          </InlineStack>
          <Divider />
          <BlockStack gap="300">
            {ticket.assigned_to ? (
              <>
                <InlineStack gap="600">
                  <BlockStack gap="200">
                    <Text variant="bodyMd" fontWeight="semibold">Assigned To</Text>
                    <BlockStack gap="100">
                      <Text variant="bodyMd">{ticket.assigned_to_name || 'Unknown'}</Text>
                      {ticket.assigned_to_email && (
                        <Text variant="bodySm" tone="subdued">{ticket.assigned_to_email}</Text>
                      )}
                      {ticket.assigned_to_role && (
                        <Badge tone="info">{ticket.assigned_to_role}</Badge>
                      )}
                    </BlockStack>
                  </BlockStack>

                  {ticket.assigned_by_name && (
                    <BlockStack gap="200">
                      <Text variant="bodyMd" fontWeight="semibold">Assigned By</Text>
                      <Text variant="bodyMd">{ticket.assigned_by_name}</Text>
                    </BlockStack>
                  )}

                  {ticket.assigned_at && (
                    <BlockStack gap="200">
                      <Text variant="bodyMd" fontWeight="semibold">Assigned At</Text>
                      <Text variant="bodySm" tone="subdued">{formatDate(ticket.assigned_at)}</Text>
                    </BlockStack>
                  )}
                </InlineStack>
              </>
            ) : (
              <Box paddingBlockStart="200">
                <Text variant="bodyMd" tone="subdued">
                  This ticket has not been assigned to anyone yet. Click "Assign Ticket" to assign it to a team member.
                </Text>
              </Box>
            )}
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Attachments Section */}
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">Attachments ({ticket.attachments?.length || 0})</Text>
          <Divider />

          {/* Upload Form */}
          <BlockStack gap="300">
            <InlineStack gap="300" blockAlign="center" wrap={false}>
              <input
                id="file-upload-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                onClick={() => document.getElementById('file-upload-input').click()}
              >
                Choose Files
              </Button>
              {selectedFiles.length > 0 && (
                <>
                  <Text variant="bodyMd" as="span">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                  </Text>
                  <Button
                    primary
                    onClick={handleFileUpload}
                    loading={uploadingFile}
                    disabled={uploadingFile}
                  >
                    Upload All
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedFiles([]);
                      document.getElementById('file-upload-input').value = '';
                    }}
                    disabled={uploadingFile}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </InlineStack>
            {selectedFiles.length > 0 && (
              <Box>
                <Text variant="bodyMd" fontWeight="semibold">Selected files:</Text>
                <Box paddingBlockStart="200">
                  <BlockStack gap="100">
                    {selectedFiles.map((file, index) => (
                      <Text key={index} variant="bodySm" tone="subdued">
                        • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      </Text>
                    ))}
                  </BlockStack>
                </Box>
              </Box>
            )}
            <Text variant="bodySm" tone="subdued">
              Supported: Images only (JPG, PNG, GIF, etc.) • Max size: 10MB per file • Multiple files allowed
            </Text>
          </BlockStack>

          <Divider />

          {/* Attachments List */}
          {ticket.attachments && ticket.attachments.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {ticket.attachments.map((attachment, index) => (
                <Box key={attachment.id || index} padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="300">
                    {/* Image Preview */}
                    {attachment.mime_type?.startsWith('image/') && (
                      <Box>
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(attachment.url, '_blank')}
                        />
                      </Box>
                    )}

                    <BlockStack gap="200">
                      <InlineStack gap="200" blockAlign="center" wrap={false}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text variant="bodyMd" fontWeight="semibold" truncate>
                            {attachment.filename}
                          </Text>
                        </div>
                        <Badge tone="info">
                          {attachment.mime_type?.split('/')[1] || 'image'}
                        </Badge>
                      </InlineStack>

                      <BlockStack gap="100">
                        <Text variant="bodySm" tone="subdued">
                          Size: {(attachment.size / 1024).toFixed(2)} KB
                        </Text>
                        <Text variant="bodySm" tone="subdued" truncate>
                          By: {attachment.uploaded_by_name || 'Unknown'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          {formatDate(attachment.uploaded_at)}
                        </Text>
                      </BlockStack>

                      <Button
                        fullWidth
                        onClick={() => window.open(attachment.url, '_blank')}
                      >
                        View Full Size
                      </Button>
                    </BlockStack>
                  </BlockStack>
                </Box>
              ))}
            </div>
          ) : (
            <Text variant="bodyMd" tone="subdued">No attachments yet</Text>
          )}
        </BlockStack>
      </Card>

      {/* Comments Section */}
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h3">Comments ({ticket.comments?.length || 0})</Text>
          <Divider />

          {/* Add Comment Form */}
          <BlockStack gap="300">
            <TextField
              label="Add Comment"
              value={newComment}
              onChange={(value) => setNewComment(value)}
              multiline={4}
              placeholder="Type your comment here..."
              autoComplete="off"
            />
            <InlineStack gap="200" align="space-between">
              <Button
                primary
                onClick={handleAddComment}
                loading={addingComment}
                disabled={!newComment.trim()}
              >
                Add Comment
              </Button>
            </InlineStack>
          </BlockStack>

          <Divider />

          {/* Comments List */}
          {ticket.comments && ticket.comments.length > 0 ? (
            <BlockStack gap="300">
              {ticket.comments.map((comment, index) => (
                <Box key={comment.id || index} padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <InlineStack gap="200">
                        <Text variant="bodyMd" fontWeight="semibold">
                          {comment.user_name || 'System'}
                          {comment.user_role && comment.user_role !== 'System' && (
                            <Text variant="bodySm" tone="subdued"> ({comment.user_role})</Text>
                          )}
                        </Text>
                        {comment.is_internal && (
                          <Badge tone="info">Internal</Badge>
                        )}
                      </InlineStack>
                      <Text variant="bodySm" tone="subdued">{formatDate(comment.created_at)}</Text>
                    </InlineStack>
                    <Text variant="bodyMd" as="div" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.content?.replace(/\\n/g, '\n') || ''}
                    </Text>
                  </BlockStack>
                </Box>
              ))}
            </BlockStack>
          ) : (
            <Text variant="bodyMd" tone="subdued">No comments yet</Text>
          )}
        </BlockStack>
      </Card>

      {/* SLA Information */}
      {ticket.sla && (
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h3">SLA Information</Text>
            <Divider />
            <InlineStack gap="600">
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="semibold">Response Due</Text>
                <Text variant="bodySm" tone={ticket.sla.first_response_at ? 'success' : 'subdued'}>
                  {formatDate(ticket.sla.response_due)}
                  {ticket.sla.first_response_at && ' ✓'}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="semibold">Resolution Due</Text>
                <Text variant="bodySm" tone="subdued">{formatDate(ticket.sla.resolution_due)}</Text>
              </BlockStack>
              {ticket.sla.escalated && (
                <BlockStack gap="100">
                  <Badge tone="critical">Escalated</Badge>
                  <Text variant="bodySm" tone="subdued">{formatDate(ticket.sla.escalated_at)}</Text>
                </BlockStack>
              )}
            </InlineStack>
          </BlockStack>
        </Card>
      )}

      {/* Assignment Modal */}
      <AssignTicketModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        ticketId={ticketId}
        currentAssignee={ticket?.assigned_to}
        onSuccess={() => {
          setAssignModalOpen(false);
          loadTicket();
        }}
        onError={(error) => {
          console.error('Assignment error:', error);
          alert(error);
        }}
      />
    </>
  );
};

export default TicketDetailPage;
